import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Users, Search } from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal that picks a cohort, shows its active roster, and lets the admin
 * add one, some, or all learners to the current event as attendees. Reuses
 * the existing `POST /events/{eventId}/attendees/import` endpoint (which
 * already handles dedupe by name).
 */
export default function AddAttendeesFromCohortModal({ open, onOpenChange, eventId, onAdded }) {
  const [cohorts, setCohorts] = useState([]);
  const [cohortsLoading, setCohortsLoading] = useState(false);
  const [selectedCohortId, setSelectedCohortId] = useState("");
  const [learners, setLearners] = useState([]);
  const [learnersLoading, setLearnersLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [saving, setSaving] = useState(false);

  // Reset the modal each time it re-opens so a previous session doesn't
  // bleed into a fresh add flow.
  useEffect(() => {
    if (!open) return;
    setSelectedCohortId("");
    setLearners([]);
    setSelected(new Set());
    setQuery("");
    setCohortsLoading(true);
    axios
      .get(`${API}/cohorts?status=active`, { withCredentials: true })
      .then(({ data }) => setCohorts(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Failed to load cohorts"))
      .finally(() => setCohortsLoading(false));
  }, [open]);

  // Whenever a cohort is picked, load its active roster.
  useEffect(() => {
    if (!selectedCohortId) {
      setLearners([]);
      setSelected(new Set());
      return;
    }
    setLearnersLoading(true);
    setSelected(new Set());
    axios
      .get(`${API}/cohorts/${selectedCohortId}/learners`, { withCredentials: true })
      .then(({ data }) => setLearners(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Failed to load cohort roster"))
      .finally(() => setLearnersLoading(false));
  }, [selectedCohortId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return learners;
    return learners.filter((l) =>
      `${l.first_name || ""} ${l.last_name || ""} ${l.email || ""}`
        .toLowerCase()
        .includes(q),
    );
  }, [learners, query]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((l) => selected.has(l.id));
  const someFilteredSelected =
    !allFilteredSelected && filtered.some((l) => selected.has(l.id));

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allFilteredSelected) {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((l) => next.delete(l.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        filtered.forEach((l) => next.add(l.id));
        return next;
      });
    }
  };

  const submit = async () => {
    const rows = learners
      .filter((l) => selected.has(l.id))
      .map((l) => ({
        first_name: l.first_name || "",
        last_name: l.last_name || "",
        email: l.email || "",
      }));
    if (!rows.length) {
      toast.error("Select at least one learner");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/events/${eventId}/attendees/import`,
        { rows },
        { withCredentials: true },
      );
      const added = data?.imported ?? 0;
      const skipped = data?.skipped ?? 0;
      toast.success(
        `Added ${added} attendee${added === 1 ? "" : "s"}` +
          (skipped ? ` · ${skipped} already on the list` : ""),
      );
      onOpenChange(false);
      if (onAdded) onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add attendees");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#11141A] border-white/10 text-white max-w-2xl max-h-[85vh] flex flex-col"
        data-testid="attendees-add-from-cohort-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-body font-bold text-lg text-white flex items-center gap-2">
            <Users className="h-4 w-4" /> Add Attendees from Cohort
          </DialogTitle>
          <p className="text-xs font-body text-[#6E7787]">
            Pick a cohort, then select individual learners or the entire roster to add as attendees.
          </p>
        </DialogHeader>

        {/* Cohort selector */}
        <div>
          <label className="text-[10px] font-body uppercase tracking-wider text-[#6E7787] mb-1 block">
            Cohort
          </label>
          <select
            value={selectedCohortId}
            onChange={(e) => setSelectedCohortId(e.target.value)}
            disabled={cohortsLoading}
            data-testid="attendees-from-cohort-select"
            className="w-full h-9 rounded-md bg-[#0A0C10] border border-white/10 text-white text-sm font-body px-2 focus:outline-none focus:border-[var(--primary-color,#4A90D9)] disabled:opacity-50"
          >
            <option value="">
              {cohortsLoading ? "Loading cohorts…" : "-- Select a cohort --"}
            </option>
            {cohorts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.status && c.status !== "active" ? ` · ${c.status}` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Roster search + select-all */}
        {selectedCohortId && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787]" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search this cohort's learners"
                data-testid="attendees-from-cohort-search"
                className="pl-9 bg-[#0A0C10] border-white/10 text-white h-9"
              />
            </div>
            {filtered.length > 0 && (
              <label className="inline-flex items-center gap-2 text-xs font-body text-[#A1A9B8] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someFilteredSelected;
                  }}
                  onChange={toggleAll}
                  data-testid="attendees-from-cohort-select-all"
                  className="h-4 w-4 rounded border-white/20 bg-transparent accent-[var(--primary-color,#4A90D9)]"
                />
                Select all ({filtered.length})
              </label>
            )}
          </div>
        )}

        {/* Roster list */}
        <div
          className="flex-1 overflow-y-auto -mx-2 px-2 min-h-[240px]"
          data-testid="attendees-from-cohort-list"
        >
          {!selectedCohortId ? (
            <div className="text-center py-12 text-sm font-body text-[#6E7787]">
              Pick a cohort above to see its roster.
            </div>
          ) : learnersLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-[#6E7787]" />
            </div>
          ) : filtered.length === 0 ? (
            <div
              className="text-center py-12 text-sm font-body text-[#6E7787]"
              data-testid="attendees-from-cohort-empty"
            >
              {learners.length === 0
                ? "This cohort has no active learners."
                : "No learners match your search."}
            </div>
          ) : (
            <ul className="space-y-1.5">
              {filtered.map((l) => {
                const isSelected = selected.has(l.id);
                return (
                  <li
                    key={l.id}
                    onClick={() => toggle(l.id)}
                    data-testid={`attendees-from-cohort-row-${l.id}`}
                    className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-all ${
                      isSelected
                        ? "border-[var(--primary-color,#4A90D9)]/60 ring-1 ring-[var(--primary-color,#4A90D9)]/30 bg-[var(--primary-color,#4A90D9)]/5"
                        : "border-white/10 hover:border-white/20 bg-[#0A0C10]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(l.id)}
                      onClick={(evt) => evt.stopPropagation()}
                      data-testid={`attendees-from-cohort-check-${l.id}`}
                      className="h-4 w-4 rounded border-white/20 bg-transparent accent-[var(--primary-color,#4A90D9)] cursor-pointer"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-body font-medium text-white truncate">
                        {l.first_name} {l.last_name}
                      </p>
                      {l.email && (
                        <p className="text-[11px] font-body text-[#6E7787] truncate">
                          {l.email}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="border-t border-white/5 pt-3">
          <div
            className="flex-1 text-xs font-body text-[#6E7787]"
            data-testid="attendees-from-cohort-selected-count"
          >
            {selected.size} selected
          </div>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="attendees-from-cohort-cancel"
            className="text-[#A1A9B8] hover:text-white hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || selected.size === 0}
            data-testid="attendees-from-cohort-confirm"
            style={{
              background: "var(--primary-color)",
              color: "var(--admin-button-fg, #fff)",
            }}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              `Add ${selected.size || ""} attendee${selected.size === 1 ? "" : "s"}`.trim()
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
